import React, { useEffect, useState } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { postUserV2 } from '../../../../services/usersService'
import { postFile } from '../../../../services/files'
import { getTemplateAll } from '../../../../services/templates'
import { getTemplateOSAll } from '../../../../services/templateOS'
import { getTemplateProposalAll } from '../../../../services/templatesProposal'
import { useToast } from '../../../components/Toats'
import { ModalDetailsCreate } from '../addCompanies/components/ModalDetailsCreate'
import { getContrastColor } from '../../../../utils/contratColor'
import styles from './addCompaniesV2.module.css'

const VISION_PRIMARY = '#ffc700'
const VISION_VARIANT = '#f1bc00'

const validationSchema = Yup.object().shape({
  user: Yup.object({
    name: Yup.string().required(),
    email: Yup.string().email().required(),
    cellphone_whatsapp: Yup.string(),
    account_type: Yup.string().oneOf(['NORMAL', 'DEMO']),
    qtd_dias_teste: Yup.number(),
  }),
  company: Yup.object({
    corporateName: Yup.string().required(),
    cnpj: Yup.string().required(),
    site: Yup.string(),
    telephone: Yup.string(),
    image: Yup.string().nullable(),
    image_dark: Yup.string().nullable(),
    image_cell: Yup.string().nullable(),
    color_primary: Yup.string(),
    color_primary_variet: Yup.string(),
  }),
  plan: Yup.object({
    qtd_plants_plano: Yup.number(),
    qtd_plants_plano_starter: Yup.number(),
    qtd_plants_gratuita: Yup.number(),
  }),
  settings: Yup.object({
    reports_client_automatics: Yup.boolean(),
    liberar_whatsapp: Yup.boolean(),
    idTemplateGeracao: Yup.string(),
    idTemplateDetalhado: Yup.string(),
    idTemplateOS: Yup.string(),
    idTemplateProposal: Yup.string(),
  }),
})

const initialValues = {
  user: {
    name: '',
    email: '',
    cellphone_whatsapp: '',
    account_type: 'NORMAL',
    qtd_dias_teste: 30,
  },
  company: {
    corporateName: '',
    cnpj: '',
    site: '',
    telephone: '',
    image: '',
    image_dark: '',
    image_cell: '',
    color_primary: VISION_PRIMARY,
    color_primary_variet: VISION_VARIANT,
  },
  plan: {
    plano: 'BASE',
    qtd_plants_plano: 0,
    qtd_plants_plano_starter: 0,
    qtd_plants_gratuita: 0,
  },
  settings: {
    reports_client_automatics: true,
    // TODO: reativar módulo WhatsApp
    // liberar_whatsapp: true,
    liberar_whatsapp: false,
    idTemplateGeracao: '',
    idTemplateDetalhado: '',
    idTemplateOS: '',
    idTemplateProposal: '',
  },
}

const maskCnpj = (value: string) =>
  value
    .replace(/\D/g, '')
    .slice(0, 14)
    .replace(/(\d{2})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1/$2')
    .replace(/(\d{4})(\d{1,2})$/, '$1-$2')

export function AddCompaniesV2({ myRefresh, setMyRefresh }: any) {
  const { toggleToast } = useToast()
  const [loading, setLoading] = useState(false)
  const [hasError, setHasError] = useState(false)
  const [showDetailsAppModal, setShowDetailsAppModal] = useState(false)
  const [saveResult, setSaveResult] = useState<any>(null)
  const [logoPreview, setLogoPreview] = useState('')
  const [logoDarkPreview, setLogoDarkPreview] = useState('')
  const [logoCellPreview, setLogoCellPreview] = useState('')
  const [uploadingLogo, setUploadingLogo] = useState<'light' | 'dark' | 'cell' | null>(null)
  const [templates, setTemplates] = useState<any[]>([])
  const [templatesOS, setTemplatesOS] = useState<any[]>([])
  const [templatesProposal, setTemplatesProposal] = useState<any[]>([])

  const formik = useFormik({
    initialValues,
    validationSchema,
    validateOnChange: false,
    onSubmit: () => {},
  })

  const loadTemplates = async () => {
    try {
      const [reports, os, proposals] = await Promise.all([
        getTemplateAll('public'),
        getTemplateOSAll('public'),
        getTemplateProposalAll('public'),
      ])
      setTemplates(Array.isArray(reports) ? reports : [])
      setTemplatesOS(Array.isArray(os) ? os : [])
      setTemplatesProposal(Array.isArray(proposals) ? proposals : [])
    } catch (error) {
      console.log(error)
    }
  }

  useEffect(() => {
    loadTemplates()
  }, [])

  const checkInputs = () => {
    const { user, company, plan } = formik.values
    if (!user.name || !user.email || !company.corporateName || !company.cnpj) {
      return false
    }
    if (user.account_type === 'DEMO' && (!Number(user.qtd_dias_teste) || Number(user.qtd_dias_teste) <= 0)) {
      return false
    }
    if (!plan.qtd_plants_plano && !plan.qtd_plants_gratuita && !plan.qtd_plants_plano_starter) {
      return false
    }
    return true
  }

  const demoExpiresLabel = () => {
    const days = Number(formik.values.user.qtd_dias_teste) || 0
    if (formik.values.user.account_type !== 'DEMO' || days <= 0) return ''
    const date = new Date()
    date.setDate(date.getDate() + days)
    return date.toLocaleDateString('pt-BR')
  }

  const changePlanQty = (field: 'qtd_plants_plano' | 'qtd_plants_plano_starter' | 'qtd_plants_gratuita', delta: number) => {
    const next = Math.max(0, Number(formik.values.plan[field] || 0) + delta)
    formik.setFieldValue(`plan.${field}`, next)
  }

  const uploadLogo = async (file: File, type: 'light' | 'dark' | 'cell') => {
    const localUrl = URL.createObjectURL(file)
    if (type === 'light') setLogoPreview(localUrl)
    else if (type === 'dark') setLogoDarkPreview(localUrl)
    else setLogoCellPreview(localUrl)

    try {
      setUploadingLogo(type)
      const formData = new FormData()
      formData.append('file', file)
      const uploaded = await postFile(formData)
      const key = uploaded?.key || ''
      if (type === 'light') {
        formik.setFieldValue('company.image', key)
        if (uploaded?.url) setLogoPreview(uploaded.url)
      } else if (type === 'dark') {
        formik.setFieldValue('company.image_dark', key)
        if (uploaded?.url) setLogoDarkPreview(uploaded.url)
      } else {
        formik.setFieldValue('company.image_cell', key)
        if (uploaded?.url) setLogoCellPreview(uploaded.url)
      }
    } catch (error) {
      console.log(error)
      toggleToast('Erro ao enviar a logo.', 'error')
    } finally {
      setUploadingLogo(null)
    }
  }

  const AddEmpresaUser = async () => {
    try {
      setLoading(true)
      const payload = {
        user: {
          name: formik.values.user.name,
          email: formik.values.user.email,
          cellphone_whatsapp: formik.values.user.cellphone_whatsapp || null,
          account_type: formik.values.user.account_type || 'NORMAL',
          qtd_dias_teste: formik.values.user.account_type === 'DEMO' ? Number(formik.values.user.qtd_dias_teste) || 0 : null,
        },
        company: {
          corporateName: formik.values.company.corporateName,
          cnpj: formik.values.company.cnpj,
          site: formik.values.company.site || null,
          telephone: formik.values.company.telephone || null,
          planos: 'BASE',
          image: formik.values.company.image || null,
          image_dark: formik.values.company.image_dark || null,
          image_cell: formik.values.company.image_cell || null,
          color_primary: formik.values.company.color_primary || null,
          color_primary_variet: formik.values.company.color_primary_variet || null,
        },
        plan: {
          plano: 'BASE',
          qtd_plants_plano: Number(formik.values.plan.qtd_plants_plano) || 0,
          qtd_plants_plano_starter: Number(formik.values.plan.qtd_plants_plano_starter) || 0,
          qtd_plants_gratuita: Number(formik.values.plan.qtd_plants_gratuita) || 0,
        },
        settings: {
          reports_client_automatics: formik.values.settings.reports_client_automatics,
          idTemplateGeracao: formik.values.settings.idTemplateGeracao || null,
          idTemplateDetalhado: formik.values.settings.idTemplateDetalhado || null,
          idTemplateOS: formik.values.settings.idTemplateOS || null,
          idTemplateProposal: formik.values.settings.idTemplateProposal || null,
        },
      }

      const result = await postUserV2(payload, 'EMPRESA')
      setSaveResult(result)
      toggleToast('Sucesso ao cadastrar a empresa', 'success')
      formik.setValues(initialValues)
      setLogoPreview('')
      setLogoDarkPreview('')
      setLogoCellPreview('')
      setShowDetailsAppModal(true)
      setMyRefresh((prev: boolean) => !prev)
    } catch (error: any) {
      console.log('erro', error)
      toggleToast(
        error?.response?.data?.message || 'Erro ao cadastrar a empresa.',
        'error'
      )
    } finally {
      setLoading(false)
    }
  }

  const submit = () => {
    setHasError(false)
    if (!checkInputs()) {
      setHasError(true)
      return
    }
    AddEmpresaUser()
  }

  const templatesGeracao = templates.filter((template) => template.type_template === 'GERACAO')
  const templatesDetalhado = templates.filter((template) => template.type_template === 'DETALHADO')

  return (
    <div className={`card card-xl-stretch mb-xl-8 ${styles.card}`} style={{ paddingBottom: '1rem' }}>
      <div
        className={`card-header border-0 pt-6 ${styles.header}`}
        role='button'
        data-bs-toggle='collapse'
        data-bs-target='#add_companies_v2'
        aria-expanded='true'
        aria-controls='add_companies_v2'
      >
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bold fs-3 mb-1'>Cadastro de Empresa</span>
          <span className='text-muted mt-1 fw-semibold fs-7'>
            Crie a empresa, o usuário, o plano e as preferências em um único fluxo
          </span>
        </h3>
        <i className='bi bi-caret-down-fill fs-7'></i>
      </div>

      <div className='collapse show' id='add_companies_v2'>
        <div className='card-body py-3'>
          <div className='separator border-gray-200 mb-5'></div>

          <div className={styles.layout}>
            <aside
              className={styles.preview}
              style={{
                backgroundColor: formik.values.company.color_primary || VISION_PRIMARY,
                color: getContrastColor(formik.values.company.color_primary || VISION_PRIMARY),
              }}
            >
              <div className={styles.previewLogos}>
                <div className={`${styles.previewLogo} ${styles.previewLogoLight}`}>
                  {logoPreview ? (
                    <img src={logoPreview} alt='Logo clara' />
                  ) : (
                    <i className='bi bi-image fs-2' style={{ opacity: 0.45 }}></i>
                  )}
                  <span className={styles.previewLogoLabel}>Logo clara</span>
                </div>
                <div className={`${styles.previewLogo} ${styles.previewLogoDark}`}>
                  {logoDarkPreview ? (
                    <img src={logoDarkPreview} alt='Logo escura' />
                  ) : (
                    <i className='bi bi-image fs-2' style={{ opacity: 0.55 }}></i>
                  )}
                  <span className={styles.previewLogoLabel}>Logo escura</span>
                </div>
                <div className={`${styles.previewLogo} ${styles.previewLogoCell}`}>
                  {logoCellPreview ? (
                    <img src={logoCellPreview} alt='Logo celular' />
                  ) : (
                    <i className='bi bi-phone fs-2' style={{ opacity: 0.45 }}></i>
                  )}
                  <span className={styles.previewLogoLabel}>Logo celular</span>
                </div>
              </div>
              <h4
                className={styles.previewName}
                style={{ color: getContrastColor(formik.values.company.color_primary || VISION_PRIMARY) }}
              >
                {formik.values.company.corporateName || 'Nome da empresa'}
              </h4>
              <p className={styles.previewMeta}>
                {formik.values.company.cnpj || '00.000.000/0000-00'}
              </p>
              <p className={styles.previewMeta}>
                {formik.values.company.site || 'www.empresa.com'}
              </p>
              <div
                className={styles.previewUser}
                style={{
                  backgroundColor: formik.values.company.color_primary_variet || VISION_VARIANT,
                  color: getContrastColor(formik.values.company.color_primary_variet || VISION_VARIANT),
                }}
              >
                <p className={styles.previewMeta} style={{ marginBottom: '0.2rem' }}>
                  {formik.values.user.name || 'Usuário administrador'}
                </p>
                <p className={styles.previewMeta}>
                  {formik.values.user.email || 'email@empresa.com'}
                </p>
                <p className={styles.previewMeta} style={{ marginTop: '0.35rem', fontWeight: 700 }}>
                  {formik.values.user.account_type === 'DEMO'
                    ? `DEMO · ${formik.values.user.qtd_dias_teste || 0} dias · até ${demoExpiresLabel() || '-'}`
                    : 'Conta NORMAL'}
                </p>
              </div>
            </aside>

            <div className={styles.sections}>
              <section className={styles.section}>
                <div className={styles.sectionTitle}>
                  <span className={styles.badge}>1</span>
                  <div>
                    <div className='fw-bold fs-6'>Empresa</div>
                    <div className='text-muted fs-8'>Dados principais da nova empresa</div>
                  </div>
                </div>
                <div className={styles.grid2}>
                  <div>
                    <label className='form-label required fw-bold fs-7'>Nome da Empresa</label>
                    <input
                      type='text'
                      className='form-control form-control-solid bg-body'
                      placeholder='Minha Empresa'
                      value={formik.values.company.corporateName}
                      onChange={formik.handleChange('company.corporateName')}
                    />
                    {!formik.values.company.corporateName && hasError && (
                      <div className={styles.error}>O nome da empresa é obrigatório.</div>
                    )}
                  </div>
                  <div>
                    <label className='form-label required fw-bold fs-7'>CNPJ</label>
                    <input
                      type='text'
                      className='form-control form-control-solid bg-body'
                      placeholder='00.000.000/0000-00'
                      value={formik.values.company.cnpj}
                      onChange={(event) => formik.setFieldValue('company.cnpj', maskCnpj(event.target.value))}
                    />
                    {!formik.values.company.cnpj && hasError && (
                      <div className={styles.error}>O CNPJ da empresa é obrigatório.</div>
                    )}
                  </div>
                  <div>
                    <label className='form-label fw-bold fs-7'>Site</label>
                    <input
                      type='text'
                      className='form-control form-control-solid bg-body'
                      placeholder='www.empresa.com'
                      value={formik.values.company.site}
                      onChange={formik.handleChange('company.site')}
                    />
                  </div>
                  <div>
                    <label className='form-label fw-bold fs-7'>Telefone</label>
                    <input
                      type='text'
                      className='form-control form-control-solid bg-body'
                      placeholder='(00) 00000-0000'
                      value={formik.values.company.telephone}
                      onChange={formik.handleChange('company.telephone')}
                    />
                  </div>
                </div>
              </section>

              <section className={styles.section}>
                <div className={styles.sectionTitle}>
                  <span className={styles.badge}>2</span>
                  <div>
                    <div className='fw-bold fs-6'>Identidade visual</div>
                    <div className='text-muted fs-8'>Logos e cores da marca</div>
                  </div>
                </div>
                <div className={styles.grid3}>
                  <label className={`${styles.logoBox} bg-body`}>
                    {logoPreview ? (
                      <img src={logoPreview} alt='Logo clara' />
                    ) : (
                      <i className='bi bi-image fs-2 text-muted'></i>
                    )}
                    <span className='fw-bold fs-7'>Logo clara</span>
                    <span className='text-muted fs-8'>
                      {uploadingLogo === 'light' ? 'Enviando...' : 'Clique para enviar'}
                    </span>
                    <input
                      type='file'
                      accept='image/*'
                      disabled={uploadingLogo !== null}
                      onChange={(event) => {
                        const file = event.target.files?.[0]
                        if (file) uploadLogo(file, 'light')
                        event.target.value = ''
                      }}
                    />
                  </label>
                  <label className={`${styles.logoBox} ${styles.logoBoxDark}`}>
                    {logoDarkPreview ? (
                      <img src={logoDarkPreview} alt='Logo escura' />
                    ) : (
                      <i className='bi bi-image fs-2 text-white'></i>
                    )}
                    <span className='fw-bold fs-7 text-white'>Logo escura</span>
                    <span className='text-white-50 fs-8'>
                      {uploadingLogo === 'dark' ? 'Enviando...' : 'Clique para enviar'}
                    </span>
                    <input
                      type='file'
                      accept='image/*'
                      disabled={uploadingLogo !== null}
                      onChange={(event) => {
                        const file = event.target.files?.[0]
                        if (file) uploadLogo(file, 'dark')
                        event.target.value = ''
                      }}
                    />
                  </label>
                  <label className={`${styles.logoBox} bg-body`}>
                    {logoCellPreview ? (
                      <img src={logoCellPreview} alt='Logo celular' />
                    ) : (
                      <i className='bi bi-phone fs-2 text-muted'></i>
                    )}
                    <span className='fw-bold fs-7'>Logo celular</span>
                    <span className='text-muted fs-8'>
                      {uploadingLogo === 'cell' ? 'Enviando...' : 'Clique para enviar'}
                    </span>
                    <input
                      type='file'
                      accept='image/*'
                      disabled={uploadingLogo !== null}
                      onChange={(event) => {
                        const file = event.target.files?.[0]
                        if (file) uploadLogo(file, 'cell')
                        event.target.value = ''
                      }}
                    />
                  </label>
                </div>
                <div className={styles.grid2} style={{ marginTop: '0.85rem' }}>
                  <div className={`${styles.colorRow} bg-body`}>
                    <input
                      type='color'
                      value={formik.values.company.color_primary}
                      onChange={formik.handleChange('company.color_primary')}
                    />
                    <div>
                      <div className='fw-bold fs-7'>Cor primária</div>
                      <div className='text-muted fs-8'>{formik.values.company.color_primary}</div>
                    </div>
                  </div>
                  <div className={`${styles.colorRow} bg-body`}>
                    <input
                      type='color'
                      value={formik.values.company.color_primary_variet}
                      onChange={formik.handleChange('company.color_primary_variet')}
                    />
                    <div>
                      <div className='fw-bold fs-7'>Cor variante</div>
                      <div className='text-muted fs-8'>{formik.values.company.color_primary_variet}</div>
                    </div>
                  </div>
                </div>
              </section>

              <section className={styles.section}>
                <div className={styles.sectionTitle}>
                  <span className={styles.badge}>3</span>
                  <div>
                    <div className='fw-bold fs-6'>Usuário administrador</div>
                    <div className='text-muted fs-8'>Acesso EMPRESA criado automaticamente</div>
                  </div>
                </div>
                <div className={styles.grid2}>
                  <div>
                    <label className='form-label required fw-bold fs-7'>Nome do Usuário</label>
                    <input
                      type='text'
                      className='form-control form-control-solid bg-body'
                      placeholder='Proprietário Solar Vision'
                      value={formik.values.user.name}
                      onChange={formik.handleChange('user.name')}
                    />
                    {!formik.values.user.name && hasError && (
                      <div className={styles.error}>O nome do usuário é obrigatório.</div>
                    )}
                  </div>
                  <div>
                    <label className='form-label required fw-bold fs-7'>E-mail do Usuário</label>
                    <input
                      type='email'
                      className='form-control form-control-solid bg-body'
                      placeholder='email@email.com' 
                      value={formik.values.user.email}
                      onChange={formik.handleChange('user.email')}
                    />
                    {!formik.values.user.email && hasError && (
                      <div className={styles.error}>O e-mail do usuário é obrigatório.</div>
                    )}
                  </div>
                  <div>
                    <label className='form-label fw-bold fs-7'>WhatsApp</label>
                    <input
                      type='text'
                      className='form-control form-control-solid bg-body'
                      placeholder='(00) 00000-0000'
                      value={formik.values.user.cellphone_whatsapp}
                      onChange={formik.handleChange('user.cellphone_whatsapp')}
                    />
                  </div>
                </div>

                <div className={styles.accountTypeWrap}>
                  <label className='form-label required fw-bold fs-7 mb-2'>Tipo de conta</label>
                  <div className={styles.accountTypeGrid}>
                    <button
                      type='button'
                      className={`${styles.accountTypeBtn} bg-body ${formik.values.user.account_type === 'NORMAL' ? styles.accountTypeBtnActive : ''}`}
                      onClick={() => formik.setFieldValue('user.account_type', 'NORMAL')}
                    >
                      <span className={styles.accountTypeRadio} />
                      <span className={styles.accountTypeContent}>
                        <span className={styles.accountTypeTitle}>NORMAL</span>
                        <span className={styles.accountTypeDesc}>Conta definitiva, sem data de expiração</span>
                      </span>
                    </button>
                    <button
                      type='button'
                      className={`${styles.accountTypeBtn} bg-body ${formik.values.user.account_type === 'DEMO' ? styles.accountTypeBtnDemo : ''}`}
                      onClick={() => {
                        formik.setFieldValue('user.account_type', 'DEMO')
                        if (!Number(formik.values.user.qtd_dias_teste) || Number(formik.values.user.qtd_dias_teste) <= 0) {
                          formik.setFieldValue('user.qtd_dias_teste', 30)
                        }
                      }}
                    >
                      <span className={styles.accountTypeRadio} />
                      <span className={styles.accountTypeContent}>
                        <span className={styles.accountTypeTitleRow}>
                          <span className={styles.accountTypeTitle}>DEMO</span>
                          <span className={styles.accountTypeBadge}>Teste</span>
                        </span>
                        <span className={styles.accountTypeDesc}>Período limitado. Expira automaticamente</span>
                      </span>
                    </button>
                  </div>

                  {formik.values.user.account_type === 'DEMO' && (
                    <div className={styles.demoPanel}>
                      <div className={styles.demoDays}>
                        <label className='form-label required fw-bold fs-7'>Dias de teste</label>
                        <div className={styles.planCounter}>
                          <button
                            type='button'
                            className='btn btn-sm btn-light'
                            onClick={() => formik.setFieldValue('user.qtd_dias_teste', Math.max(1, Number(formik.values.user.qtd_dias_teste || 0) - 1))}
                          >
                            -
                          </button>
                          <input
                            type='number'
                            min={1}
                            className='form-control form-control-solid bg-body'
                            placeholder='30'
                            value={formik.values.user.qtd_dias_teste}
                            onChange={formik.handleChange('user.qtd_dias_teste')}
                          />
                          <button
                            type='button'
                            className='btn btn-sm btn-light'
                            onClick={() => formik.setFieldValue('user.qtd_dias_teste', Number(formik.values.user.qtd_dias_teste || 0) + 1)}
                          >
                            +
                          </button>
                        </div>
                        <div className={styles.demoPresets}>
                          {[15, 30, 60, 90].map((days) => (
                            <button
                              key={days}
                              type='button'
                              className={`${styles.demoPreset} ${Number(formik.values.user.qtd_dias_teste) === days ? styles.demoPresetActive : ''}`}
                              onClick={() => formik.setFieldValue('user.qtd_dias_teste', days)}
                            >
                              {days} dias
                            </button>
                          ))}
                        </div>
                        {(!Number(formik.values.user.qtd_dias_teste) || Number(formik.values.user.qtd_dias_teste) <= 0) && hasError && (
                          <div className={styles.error}>Informe a quantidade de dias de teste.</div>
                        )}
                      </div>
                      <div className={styles.demoExpire}>
                        <span className={styles.demoExpireLabel}>Expira em</span>
                        <span className={styles.demoExpireDate}>{demoExpiresLabel() || '-'}</span>
                      </div>
                    </div>
                  )}
                </div>
              </section>

              <section className={styles.section}>
                <div className={styles.sectionTitle}>
                  <span className={styles.badge}>4</span>
                  <div>
                    <div className='fw-bold fs-6'>Plano da empresa</div>
                    <div className='text-muted fs-8'>Quantidade de usinas por tipo de plano</div>
                  </div>
                </div>
                <div className={styles.grid3}>
                  {[
                    { label: 'PREMIUM', field: 'qtd_plants_plano' as const, color: '#7239EA' },
                    { label: 'STARTER', field: 'qtd_plants_plano_starter' as const, color: '#009EF7' },
                    { label: 'BÁSICAS', field: 'qtd_plants_gratuita' as const, color: '#50CD89' },
                  ].map((plan) => (
                    <div className={`${styles.planCard} bg-body`} key={plan.field}>
                      <div className={styles.planHead}>
                        <span className='fw-bold fs-7'>{plan.label}</span>
                        <span className='bullet' style={{ backgroundColor: plan.color, width: 10, height: 10, borderRadius: 99 }}></span>
                      </div>
                      <div className={styles.planCounter}>
                        <button
                          type='button'
                          className='btn btn-sm btn-light'
                          onClick={() => changePlanQty(plan.field, -1)}
                        >
                          -
                        </button>
                        <input
                          type='number'
                          min={0}
                          className='form-control form-control-solid bg-body'
                          value={formik.values.plan[plan.field]}
                          onChange={formik.handleChange(`plan.${plan.field}`)}
                        />
                        <button
                          type='button'
                          className='btn btn-sm btn-light'
                          onClick={() => changePlanQty(plan.field, 1)}
                        >
                          +
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                {!formik.values.plan.qtd_plants_plano &&
                  !formik.values.plan.qtd_plants_plano_starter &&
                  !formik.values.plan.qtd_plants_gratuita &&
                  hasError && (
                    <div className={styles.error}>Informe a quantidade de usinas em pelo menos 1 dos planos.</div>
                  )}
              </section>

              <section className={styles.section}>
                <div className={styles.sectionTitle}>
                  <span className={styles.badge}>5</span>
                  <div>
                    <div className='fw-bold fs-6'>Preferências</div>
                    <div className='text-muted fs-8'>Templates e automações iniciais</div>
                  </div>
                </div>
                <div className={styles.grid2}>
                  <label className={`${styles.toggleCard} ${styles.toggleCardFull} bg-body`}>
                    <span>
                      <span className='fw-bold fs-7 d-block'>Relatórios automáticos</span>
                      <span className='text-muted fs-8'>Disponibilizar relatórios para os clientes</span>
                    </span>
                    <div className='form-check form-switch form-check-custom form-check-solid'>
                      <input
                        className='form-check-input'
                        type='checkbox'
                        checked={formik.values.settings.reports_client_automatics}
                        onChange={formik.handleChange('settings.reports_client_automatics')}
                      />
                    </div>
                  </label>
                  {/* TODO: reativar módulo WhatsApp
                  <label className={`${styles.toggleCard} bg-body`}>
                    <span>
                      <span className='fw-bold fs-7 d-block'>Liberar WhatsApp</span>
                      <span className='text-muted fs-8'>Habilitar o módulo de WhatsApp da empresa</span>
                    </span>
                    <div className='form-check form-switch form-check-custom form-check-solid'>
                      <input
                        className='form-check-input'
                        type='checkbox'
                        checked={formik.values.settings.liberar_whatsapp}
                        onChange={formik.handleChange('settings.liberar_whatsapp')}
                      />
                    </div>
                  </label>
                  */}
                  <div>
                    <label className='form-label fw-bold fs-7'>Template de geração</label>
                    <select
                      className='form-select form-select-solid bg-body'
                      value={formik.values.settings.idTemplateGeracao}
                      onChange={formik.handleChange('settings.idTemplateGeracao')}
                    >
                      <option value=''>Padrão público</option>
                      {templatesGeracao.map((template) => (
                        <option key={template.id} value={template.id}>
                          {template.description}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className='form-label fw-bold fs-7'>Template detalhado</label>
                    <select
                      className='form-select form-select-solid bg-body'
                      value={formik.values.settings.idTemplateDetalhado}
                      onChange={formik.handleChange('settings.idTemplateDetalhado')}
                    >
                      <option value=''>Padrão público</option>
                      {templatesDetalhado.map((template) => (
                        <option key={template.id} value={template.id}>
                          {template.description}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className='form-label fw-bold fs-7'>Template de O.S.</label>
                    <select
                      className='form-select form-select-solid bg-body'
                      value={formik.values.settings.idTemplateOS}
                      onChange={formik.handleChange('settings.idTemplateOS')}
                    >
                      <option value=''>Padrão público</option>
                      {templatesOS.map((template) => (
                        <option key={template.id} value={template.id}>
                          {template.description}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className='form-label fw-bold fs-7'>Template de proposta</label>
                    <select
                      className='form-select form-select-solid bg-body'
                      value={formik.values.settings.idTemplateProposal}
                      onChange={formik.handleChange('settings.idTemplateProposal')}
                    >
                      <option value=''>Padrão público</option>
                      {templatesProposal.map((template) => (
                        <option key={template.id} value={template.id}>
                          {template.description}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>

        <div className='card-footer d-flex justify-content-end py-6 px-9'>
          <button type='button' disabled={loading} onClick={submit} className='btn btn-primary'>
            {!loading && 'Salvar Empresa'}
            {loading && (
              <span className='indicator-progress' style={{ display: 'block' }}>
                Salvando...{' '}
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
        </div>
      </div>

      <ModalDetailsCreate
        show={showDetailsAppModal}
        data={saveResult ? saveResult : undefined}
        handleClose={() => {
          setSaveResult(null)
          setShowDetailsAppModal(false)
          setMyRefresh((prev: boolean) => !prev)
        }}
      />
    </div>
  )
}
